import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useNavigate, Link } from "react-router-dom";
import IconAction from "@/components/IconAction";
import { Banknote, FileText, Play, Calendar, AlertTriangle, ChevronDown, ChevronUp, Hourglass, XCircle, Undo2 } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const MONTH_FR = ["", "January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

export default function Billing() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null); // {mission_id, year, month, summary}
  const [form, setForm] = useState({ po_number: "", due_days: 60, invoice_date: "" });
  const [generating, setGenerating] = useState(false);
  const [notReady, setNotReady] = useState({ year: null, month: null, missions: [] });
  const [notReadyOpen, setNotReadyOpen] = useState(true);
  // Iter-234 — dismissed queue entries (cancel/restore).
  const [dismissed, setDismissed] = useState([]);
  const [dismissedOpen, setDismissedOpen] = useState(false);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const [pending, nr, dm] = await Promise.all([
        api.get("/billing/pending"),
        api.get("/billing/missions-not-ready").catch(() => ({ data: { missions: [] } })),
        api.get("/billing/dismissed").catch(() => ({ data: { items: [] } })),
      ]);
      setRows(pending.data);
      setNotReady(nr.data || { missions: [] });
      setDismissed(dm.data?.items || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Error loading billing queue"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const [dismissDialog, setDismissDialog] = useState(null); // {row, reason, detail}
  const dismissRow = (row) => setDismissDialog({ row, reason: "Geste commercial", detail: "" });

  const submitDismiss = async () => {
    if (!dismissDialog) return;
    const { row, reason, detail } = dismissDialog;
    const fullReason = detail.trim() ? `${reason} — ${detail.trim()}` : reason;
    if (reason === "Autre" && !detail.trim()) { toast.error("Précisez le motif"); return; }
    try {
      await api.post("/billing/dismiss", { mission_id: row.mission_id, year: row.year, month: row.month, reason: fullReason });
      toast.success("Ligne retirée de la file de facturation");
      setDismissDialog(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const restoreRow = async (d) => {
    try {
      await api.post("/billing/restore", { mission_id: d.mission_id, year: d.year, month: d.month });
      toast.success("Ligne restaurée dans la file");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const openDetail = async (row) => {
    try {
      const r = await api.get(`/missions/${row.mission_id}/billing/check?year=${row.year}&month=${row.month}`);
      setDetail({ ...row, summary: r.data });
      // Iter-59: prefill PO number from the mission record (saved on previous billing) and mark
      // it required. The backend rejects empty values; the dialog will block Generate too.
      setForm({
        po_number: r.data?.mission_po_number || "",
        due_days: 60,
        invoice_date: new Date().toISOString().slice(0, 10),
      });
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const generate = async () => {
    if (!detail) return;
    if (!(form.po_number || "").trim()) {
      toast.error("Le numéro du bon de commande est obligatoire pour générer la facture.");
      return;
    }
    setGenerating(true);
    try {
      const r = await api.post(`/missions/${detail.mission_id}/billing/generate`, {
        year: detail.year, month: detail.month,
        po_number: (form.po_number || "").trim(), due_days: parseInt(form.due_days, 10) || 60,
        invoice_date: form.invoice_date || null,
      });
      toast.success(`Invoice ${r.data.number} generated`);
      setDetail(null);
      load();
      // Auto-nav to invoices page to download/share
      setTimeout(() => navigate("/invoices"), 600);
    } catch (e) {
      const detailErr = e?.response?.data?.detail;
      if (detailErr === "po_number_required") {
        toast.error("Le numéro du bon de commande est obligatoire pour générer la facture.");
      } else {
        toast.error(detailErr || "Generation failed");
      }
    }
    finally { setGenerating(false); }
  };

  return (
    <div className="p-4 md:p-8 lg:p-12 max-w-[1400px]" data-testid="billing-page">
      <div className="mb-8">
        <div className="text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-3 font-mono">Finance</div>
        <h1 className="font-heading text-2xl md:text-4xl font-bold tracking-tight">Billing queue</h1>
        <p className="text-zinc-500 mt-3 max-w-2xl">Missions whose CRAs are fully approved for a given month and that haven't been invoiced yet. Open a row to preview and generate the invoice.</p>
      </div>

      {loading && <div className="text-zinc-500">Loading…</div>}

      {/* Empty state moved BEFORE the alerts (iter-34 user request) */}
      {!loading && rows.length === 0 && (
        <div className="bg-white border border-dashed border-zinc-200 rounded-lg p-16 text-center mb-6" data-testid="billing-empty">
          <Banknote size={32} className="text-zinc-400 mx-auto mb-3" />
          <div className="text-sm text-zinc-600">No mission ready to invoice right now.</div>
          <div className="text-xs text-zinc-500 mt-1">A mission appears here once every collaborator's CRA is approved for the month and no invoice exists yet.</div>
        </div>
      )}

      {/* ----- Iter-55: billable missions table moved ABOVE the "Pas encore prêt à facturer" alerts ----- */}
      {!loading && rows.length > 0 && (
        <div className="bg-white border border-zinc-200 rounded-lg md:overflow-x-auto md:overflow-hidden mb-6">
          <div className="hidden md:grid md:grid-cols-12 md:min-w-[760px] px-5 py-3 text-[10px] uppercase tracking-[0.2em] text-zinc-500 border-b border-zinc-200 font-mono">
            <div className="col-span-4">Mission</div>
            <div className="col-span-3">Client</div>
            <div className="col-span-2">Period</div>
            <div className="col-span-2 text-right">Total HT</div>
            <div className="col-span-1 text-right">Open</div>
          </div>
          {rows.map((r) => {
            const lock = r.cra_export_lock;
            const isLocked = !!lock;
            return (
            <div
              key={`${r.mission_id}-${r.year}-${r.month}`}
              onClick={() => !isLocked && openDetail(r)}
              data-testid={`billing-row-${r.mission_id}-${r.year}-${r.month}`}
              className={`w-full text-left flex flex-col gap-2 md:grid md:grid-cols-12 md:items-center md:gap-0 px-5 py-4 border-b border-zinc-100 last:border-b-0 transition-colors ${isLocked ? "bg-amber-50/60 cursor-not-allowed" : "hover:bg-zinc-50 cursor-pointer"}`}
            >
              <div className="col-span-4 font-medium truncate flex items-center gap-2">
                {r.mission_title}
                {isLocked && (
                  <span data-testid={`billing-lock-badge-${r.mission_id}`} className="px-1.5 py-0.5 rounded text-[9px] uppercase tracking-wider font-mono bg-amber-200 text-amber-900">Locked until {lock.billing_unlocks_at}</span>
                )}
              </div>
              <div className="col-span-3 text-sm text-zinc-700 truncate">{r.client_name}</div>
              <div className="col-span-2 text-xs font-mono text-zinc-700 flex items-center gap-1.5"><Calendar size={12} /> {MONTH_FR[r.month]} {r.year}</div>
              <div className="col-span-2 text-right text-sm font-mono font-medium">{r.total_ht.toLocaleString()} {r.currency}</div>
              <div className="col-span-1 flex items-center justify-end gap-2">
                {/* Iter-234 — dismiss this pending billing entry */}
                <IconAction icon={XCircle} tone="danger" testid={`billing-dismiss-${r.mission_id}-${r.year}-${r.month}`} label="Annuler cette facturation en cours (restaurable)" onClick={(e) => { e.stopPropagation(); dismissRow(r); }} />
                <span className="text-xs text-zinc-400">{isLocked ? "—" : "→"}</span>
              </div>
            </div>
            );
          })}
        </div>
      )}

      {/* ----- Iter-234: dismissed billing entries (restore) ----- */}
      {!loading && dismissed.length > 0 && (
        <div className="mb-6 bg-zinc-50 border border-zinc-200 rounded-lg" data-testid="billing-dismissed-block">
          <button
            onClick={() => setDismissedOpen((o) => !o)}
            data-testid="billing-dismissed-toggle"
            className="w-full flex items-center gap-3 px-5 py-4 text-left"
          >
            <XCircle size={18} className="text-zinc-500 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="font-heading font-bold text-zinc-800">
                Facturations annulées · {dismissed.length}
              </div>
              <div className="text-xs text-zinc-500 mt-0.5">
                Lignes retirées manuellement de la file — restaurables à tout moment.
              </div>
            </div>
            {dismissedOpen ? <ChevronUp size={16} className="text-zinc-500" /> : <ChevronDown size={16} className="text-zinc-500" />}
          </button>
          {dismissedOpen && (
            <div className="px-5 pb-5 space-y-2">
              {dismissed.map((d) => (
                <div
                  key={`${d.mission_id}-${d.year}-${d.month}`}
                  data-testid={`billing-dismissed-row-${d.mission_id}-${d.year}-${d.month}`}
                  className="flex items-center gap-3 px-4 py-3 bg-white border border-zinc-200 rounded-md"
                >
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm truncate">{d.mission_title || d.mission_id}</div>
                    <div className="text-[11px] text-zinc-500 font-mono">
                      {d.client_name} · {MONTH_FR[d.month]} {d.year} · annulée par {d.dismissed_by_name || "—"}
                    </div>
                    {d.reason && (
                      <div className="text-[11px] text-amber-800 mt-0.5" data-testid={`billing-dismissed-reason-${d.mission_id}-${d.year}-${d.month}`}>
                        Motif : {d.reason}
                      </div>
                    )}
                  </div>
                  <IconAction icon={Undo2} testid={`billing-restore-${d.mission_id}-${d.year}-${d.month}`} label="Restaurer cette facturation" onClick={() => restoreRow(d)} />
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ----- Zoom on missions NOT ready to invoice ----- */}
      {!loading && notReady.missions && notReady.missions.length > 0 && (
        <div className="mb-6 bg-amber-50 border border-amber-300 rounded-lg" data-testid="billing-not-ready-block">
          <button
            onClick={() => setNotReadyOpen((o) => !o)}
            data-testid="billing-not-ready-toggle"
            className="w-full flex items-center gap-3 px-5 py-4 text-left"
          >
            <Hourglass size={18} className="text-amber-700 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="font-heading font-bold text-amber-900">
                Pas encore prêt à facturer · {notReady.missions.length} mission{notReady.missions.length > 1 ? "s" : ""}
                {notReady.month && (
                  <span className="ml-2 text-xs font-mono font-normal text-amber-800/80">
                    {MONTH_FR[notReady.month]} {notReady.year}
                  </span>
                )}
              </div>
              <div className="text-xs text-amber-900/70 mt-0.5">
                Active missions qui attendent des CRA, un PO, un pays ou la validation CEO avant de pouvoir générer la facture client.
              </div>
            </div>
            {notReadyOpen ? <ChevronUp size={16} className="text-amber-700" /> : <ChevronDown size={16} className="text-amber-700" />}
          </button>
          {notReadyOpen && (
            <div className="px-5 pb-5 space-y-2">
              {notReady.missions.map((m) => (
                <Link
                  key={m.mission_id}
                  to={`/missions/${m.mission_id}`}
                  data-testid={`not-ready-row-${m.mission_id}`}
                  className="flex items-start gap-3 px-4 py-3 bg-white/70 border border-amber-200 rounded-md hover:bg-white transition-colors"
                >
                  <span className="w-2 h-2 rounded-full mt-2 shrink-0" style={{ background: m.color || "#a16207" }} />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm text-amber-950 truncate">{m.title}</div>
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {m.reasons.map((r, i) => (
                        <span
                          key={`${m.mission_id}-${i}`}
                          data-testid={`not-ready-reason-${r.code}-${m.mission_id}`}
                          className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 bg-amber-200/70 text-amber-900 rounded font-mono"
                        >
                          <AlertTriangle size={10} /> {r.label}
                        </span>
                      ))}
                    </div>
                  </div>
                  <span className="text-xs text-amber-700 shrink-0">→</span>
                </Link>
              ))}
            </div>
          )}
        </div>
      )}

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-5xl w-[95vw] lg:w-[80vw]" data-testid="billing-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><FileText size={16} /> Invoice preview</DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="space-y-4 py-2">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                <Meta label="Mission" value={detail.mission_title} />
                <Meta label="Client" value={detail.client_name} />
                <Meta label="Period" value={`${MONTH_FR[detail.month]} ${detail.year}`} />
                <Meta label="Currency" value={detail.currency} mono />
              </div>

              <div className="bg-white/40 border border-zinc-200 rounded-md overflow-x-auto">
                <div className="grid grid-cols-12 min-w-[520px] px-4 py-2 text-[10px] uppercase tracking-widest text-zinc-500 font-mono border-b border-zinc-200">
                  <div className="col-span-6">Role / prestation</div>
                  <div className="col-span-2 text-right">JH</div>
                  <div className="col-span-2 text-right">TJM</div>
                  <div className="col-span-2 text-right">Total HT</div>
                </div>
                {detail.summary?.lines?.map((l) => (
                  <div key={l.role_id} className="grid grid-cols-12 items-center min-w-[520px] px-4 py-2 text-xs border-b border-zinc-100 last:border-b-0">
                    <div className="col-span-6 truncate">{l.role_name}</div>
                    <div className="col-span-2 text-right font-mono">{l.quantity_jh}</div>
                    <div className="col-span-2 text-right font-mono">{l.tjm.toLocaleString()}</div>
                    <div className="col-span-2 text-right font-mono font-medium">{l.total_ht.toLocaleString()}</div>
                  </div>
                ))}
              </div>

              <div className="bg-white/40 border border-zinc-200 rounded-md p-3 space-y-1">
                <Row label="Total HT" value={`${(detail.summary?.totals?.total_ht || 0).toLocaleString()} ${detail.currency}`} />
                <Row label={`VAT (${((detail.summary?.totals?.vat_rate || 0) * 100).toFixed(0)}%)`} value={`${(detail.summary?.totals?.total_vat || 0).toLocaleString()} ${detail.currency}`} />
                <Row label="Total TTC" value={`${(detail.summary?.totals?.total_ttc || 0).toLocaleString()} ${detail.currency}`} bold />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 pt-2 border-t border-zinc-100">
                <div><Label>Invoice date</Label><Input type="date" value={form.invoice_date} onChange={(e) => setForm({ ...form, invoice_date: e.target.value })} data-testid="billing-date" /></div>
                <div><Label>Due days</Label><Input type="number" value={form.due_days} onChange={(e) => setForm({ ...form, due_days: e.target.value })} data-testid="billing-due-days" /></div>
                <div>
                  <Label>PO number <span className="text-red-600">*</span></Label>
                  <Input
                    value={form.po_number}
                    onChange={(e) => setForm({ ...form, po_number: e.target.value })}
                    data-testid="billing-po"
                    placeholder="PO-2026-XXX"
                    className={!(form.po_number || "").trim() ? "border-red-300 focus-visible:ring-red-300" : ""}
                  />
                </div>
              </div>
              {!(form.po_number || "").trim() && (
                <div
                  data-testid="billing-po-missing-alert"
                  className="flex items-start gap-3 mt-3 p-3 bg-amber-50 border border-amber-300 rounded-md"
                >
                  <AlertTriangle size={16} className="text-amber-700 shrink-0 mt-0.5" />
                  <div className="text-xs text-amber-900 leading-relaxed">
                    <strong>Le numéro du bon de commande n'a pas été renseigné.</strong>
                    <span> Il est obligatoire pour l'édition de la facture. Renseignez-le dans le champ ci-dessus — il sera également enregistré sur la mission pour les prochaines facturations.</span>
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setDetail(null)} className="px-4 h-10 text-sm hover:bg-zinc-100 rounded-md">Cancel</button>
            <button onClick={generate} disabled={generating || !(form.po_number || "").trim()} data-testid="billing-generate" className="inline-flex items-center gap-2 px-4 h-10 bg-zinc-950 text-white text-sm rounded-md hover:bg-zinc-800 disabled:opacity-50">
              <Play size={13} /> {generating ? "Generating…" : "Generate invoice"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Iter-235 — dismiss dialog: reason is REQUIRED to trace the decision. */}
      <Dialog open={!!dismissDialog} onOpenChange={(o) => { if (!o) setDismissDialog(null); }}>
        <DialogContent data-testid="billing-dismiss-dialog">
          <DialogHeader>
            <DialogTitle>Annuler la facturation en cours</DialogTitle>
          </DialogHeader>
          {dismissDialog && (
            <div className="space-y-3 py-1 text-sm">
              <p className="text-zinc-600">
                « {dismissDialog.row.mission_title} — {MONTH_FR[dismissDialog.row.month]} {dismissDialog.row.year} » sera retirée de la file (restaurable à tout moment).
              </p>
              <div>
                <Label>Motif <span className="text-red-600">*</span></Label>
                <select
                  value={dismissDialog.reason}
                  onChange={(e) => setDismissDialog({ ...dismissDialog, reason: e.target.value })}
                  data-testid="billing-dismiss-reason"
                  className="w-full h-10 px-3 border border-zinc-300 rounded-md text-sm bg-white"
                >
                  <option value="Geste commercial">Geste commercial</option>
                  <option value="Facturation groupée le mois prochain">Facturation groupée le mois prochain</option>
                  <option value="Litige">Litige</option>
                  <option value="Erreur de saisie CRA">Erreur de saisie CRA</option>
                  <option value="Autre">Autre</option>
                </select>
              </div>
              <div>
                <Label>Précision {dismissDialog.reason === "Autre" ? <span className="text-red-600">*</span> : "(optionnel)"}</Label>
                <Input
                  value={dismissDialog.detail}
                  onChange={(e) => setDismissDialog({ ...dismissDialog, detail: e.target.value })}
                  placeholder="Détail du motif…"
                  data-testid="billing-dismiss-detail"
                  maxLength={300}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setDismissDialog(null)} className="px-4 h-10 text-sm hover:bg-zinc-100 rounded-md">Cancel</button>
            <button onClick={submitDismiss} data-testid="billing-dismiss-confirm" className="inline-flex items-center gap-2 px-4 h-10 bg-red-600 text-white text-sm rounded-md hover:bg-red-700">
              <XCircle size={14} /> Annuler la facturation
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Meta({ label, value, mono }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">{label}</div>
      <div className={`text-sm ${mono ? "font-mono" : ""} text-zinc-900 truncate`}>{value}</div>
    </div>
  );
}
function Row({ label, value, bold }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className={bold ? "font-semibold" : "text-zinc-600"}>{label}</span>
      <span className={`font-mono ${bold ? "font-bold" : ""}`}>{value}</span>
    </div>
  );
}
